/**
 * @fileoverview Custom hook for User Management business logic
 */
import { useState, useCallback, useEffect } from "react";
import { fetchData, HTTP_STATUS, intl } from "dmsbasereact";
import Swal from "sweetalert2";

/**
 * Creates a new user
 * @param {object} payload - User data
 * @returns {Promise<boolean>} Success status
 */
const createUserApi = async (payload) => {
  try {
    const response = await fetchData("/user_management/", "POST", true, payload);
    if (response.status !== HTTP_STATUS.OK_200 && response.status !== HTTP_STATUS.CREATED_201) {
      throw new Error(response?.data?.detail || "Failed to create user.");
    }
    Swal.fire({
      icon: "success",
      title: intl.get("Success").d("Success"),
      text: intl.get("UserCreatedSuccess").d("User created successfully."),
    });
    return true;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: intl.get("Error").d("Error"),
      text: error?.response?.data?.detail || error.message,
    });
    return false;
  }
};

/**
 * Updates user with full data
 * @param {number} userPk - User primary key
 * @param {object} payload - Full user data
 * @returns {Promise<object|null>} Updated user data or null
 */
const updateUserFullApi = async (userPk, payload) => {
  try {
    // Use fetchData with toast enabled (last param = true)
    const response = await fetchData(`/user_management/${userPk}/full`, "PUT", true, payload, false, true);
    if (response.status === HTTP_STATUS.OK_200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Deletes a user
 * @param {object} row - User row data
 * @returns {Promise<boolean>} Success status
 */
const deleteUserApi = async (row) => {
  const result = await Swal.fire({
    title: intl.get("DeleteUser").d("Delete User"),
    html: `
      <p>${intl.get("DeleteUserConfirmation").d("Are you sure you want to delete this user?")}</p>
      <p><strong>${row.user_name}</strong> (${row.user_login})</p>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: intl.get("Delete").d("Delete"),
    cancelButtonText: intl.get("Cancel").d("Cancel"),
  });

  if (!result.isConfirmed) return false;

  try {
    const response = await fetchData(`/user_management/${row.pk}`, "DELETE", true);
    if (response.status !== HTTP_STATUS.OK_200) {
      throw new Error(response?.data?.detail || "Failed to delete user.");
    }
    Swal.fire({
      icon: "success",
      title: intl.get("Success").d("Success"),
      text: intl.get("UserDeletedSuccess").d("User deleted successfully."),
    });
    return true;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: intl.get("Error").d("Error"),
      text: error?.response?.data?.detail || error.message,
    });
    return false;
  }
};

/**
 * Fetches user details
 * @param {number} userPk - User primary key
 * @returns {Promise<object|null>} User details or null
 */
const fetchUserDetailsApi = async (userPk) => {
  try {
    const response = await fetchData(`/user_management/${userPk}`, "GET", true);
    if (response.status === HTTP_STATUS.OK_200) {
      return response.data;
    }
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToLoadUserDetails").d("Failed to load user details"),
      text: response?.data?.detail || "",
    });
    return null;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToLoadUserDetails").d("Failed to load user details"),
      text: error?.response?.data?.detail || error.message,
    });
    return null;
  }
};

/**
 * Blocks a user (kept for backwards compatibility, but modal is preferred)
 * @param {object} row - User row data
 * @returns {Promise<boolean>} Success status
 */
const blockUserApi = async (row) => {
  const result = await Swal.fire({
    title: intl.get("BlockUser").d("Block User"),
    html: `
      <p>${intl.get("BlockUserConfirmation").d("Are you sure you want to block this user?")}</p>
      <p><strong>${row.user_name}</strong> (${row.user_login})</p>
      <input id="swal-block-reason" class="swal2-input" placeholder="${intl.get("ReasonOptional").d("Reason (optional)")}">
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: intl.get("Block").d("Block"),
    cancelButtonText: intl.get("Cancel").d("Cancel"),
    preConfirm: () => document.getElementById("swal-block-reason").value,
  });

  if (!result.isConfirmed) return false;

  try {
    const response = await fetchData(
      `/user_management/${row.pk}/block`,
      "POST",
      true,
      { reason: result.value || null },
      false,
      true
    );
    if (response.status === HTTP_STATUS.OK_200) {
      Swal.fire({
        icon: "success",
        title: intl.get("UserBlocked").d("User Blocked"),
        text: intl.get("UserBlockedSuccess").d("User has been blocked successfully."),
      });
      return true;
    }
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToBlockUser").d("Failed to block user"),
      text: response?.data?.detail || "",
    });
    return false;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToBlockUser").d("Failed to block user"),
      text: error?.response?.data?.detail || error.message,
    });
    return false;
  }
};

/**
 * Unblocks a user
 * @param {object} row - User row data
 * @returns {Promise<boolean>} Success status
 */
const unblockUserApi = async (row) => {
  const result = await Swal.fire({
    title: intl.get("UnblockUser").d("Unblock User"),
    html: `
      <p>${intl.get("UnblockUserConfirmation").d("Are you sure you want to unblock this user?")}</p>
      <p><strong>${row.user_name}</strong> (${row.user_login})</p>
      <p class="swal2-text" style="color: #666; font-size: 14px;">
        ${intl.get("UnblockUserEmailNotice").d("A new password will be generated and sent to the user's email address.")}
      </p>
    `,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: intl.get("Unblock").d("Unblock"),
    cancelButtonText: intl.get("Cancel").d("Cancel"),
  });

  if (!result.isConfirmed) return false;

  try {
    const response = await fetchData(
      `/user_management/${row.pk}/unblock`,
      "POST",
      true,
      {},
      false,
      true
    );
    if (response.status === HTTP_STATUS.OK_200) {
      Swal.fire({
        icon: "success",
        title: intl.get("UserUnblocked").d("User Unblocked"),
        text: response?.data?.message || intl.get("UserUnblockedSuccess").d("User has been unblocked successfully."),
      });
      return true;
    }
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToUnblockUser").d("Failed to unblock user"),
      text: response?.data?.detail || "",
    });
    return false;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: intl.get("FailedToUnblockUser").d("Failed to unblock user"),
      text: error?.response?.data?.detail || error.message,
    });
    return false;
  }
};

/**
 * Fetches all available roles
 * @returns {Promise<Array>} Roles array
 */
const fetchRolesApi = async () => {
  try {
    const response = await fetchData("/user_management/role/all", "GET", true);
    if (response.status === HTTP_STATUS.OK_200) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error("Failed to load roles:", error);
    return [];
  }
};

/**
 * Custom hook that manages user management state and operations
 * @returns {object} User management state and handlers
 */
export const useUserManagement = () => {
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    const roles = await fetchRolesApi();
    setAvailableRoles(roles);
    setLoadingRoles(false);
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const createUser = useCallback(async (payload) => {
    return createUserApi(payload);
  }, []);

  const updateUserFull = useCallback(async (userPk, payload) => {
    return updateUserFullApi(userPk, payload);
  }, []);

  const deleteUser = useCallback(async (row) => {
    return deleteUserApi(row);
  }, []);

  const fetchUserDetails = useCallback(async (userPk) => {
    return fetchUserDetailsApi(userPk);
  }, []);

  const blockUser = useCallback(async (row) => {
    return blockUserApi(row);
  }, []);

  const unblockUser = useCallback(async (row) => {
    return unblockUserApi(row);
  }, []);

  return {
    availableRoles,
    loadingRoles,
    createUser,
    updateUserFull,
    deleteUser,
    fetchUserDetails,
    blockUser,
    unblockUser,
  };
};
